import React from 'react';
import { Event } from '../../api/client';
import { PencilIcon, TrashIcon, EnvelopeIcon, ArchiveBoxIcon, ArrowPathIcon, EyeIcon, EyeSlashIcon, EllipsisVerticalIcon, UserPlusIcon, DocumentArrowDownIcon, PhotoIcon, StarIcon } from '@heroicons/react/24/outline';
import Dropdown from '../ui/Dropdown';

interface EventHeaderProps {
  event: Event;
  editing: boolean;
  onToggleEdit: () => void;
  onToggleStatus: () => void;
  onArchive: () => void;
  onRestore: () => void;
  onDelete: () => void;
  onEmailClick: () => void;
  onEmailInviteesClick?: () => void;
  onInviteClick?: () => void;
  onExportClick?: () => void;
  onCreateMediaClick?: () => void;
  onSponsorsClick?: () => void;
}

const EventHeader: React.FC<EventHeaderProps> = ({
  event,
  editing,
  onToggleEdit,
  onToggleStatus,
  onArchive,
  onRestore,
  onDelete,
  onEmailClick,
  onEmailInviteesClick,
  onInviteClick,
  onExportClick,
  onCreateMediaClick,
  onSponsorsClick,
}) => {
  // People still in the invitation phase (invited or opened, not yet responded)
  const openInvitationCount = (event.invitation_sent_count || 0) - (event.invitation_declined_count || 0);
  return (
    <div className="flex justify-between items-start mb-4">
      <div>
        <h2 className="text-xl font-semibold">Event Details</h2>
        <div className="flex gap-2 mt-1">
          {event.status === 'draft' && (
            <span className="inline-block px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded">
              Draft - Not Published
            </span>
          )}
          {event.status === 'active' && (
            <span className="inline-block px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
              Published
            </span>
          )}
          {event.status === 'archived' && (
            <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
              Locked
            </span>
          )}
        </div>
      </div>
      <div className="flex gap-2">
        {/* Primary action button based on status */}
        {event.status === 'draft' && (
          <button
            onClick={onToggleStatus}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center gap-2 text-sm font-medium"
          >
            <EyeIcon className="h-4 w-4" />
            Publish
          </button>
        )}
        {event.status === 'archived' && (
          <button
            onClick={onRestore}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center gap-2 text-sm font-medium"
          >
            <ArrowPathIcon className="h-4 w-4" />
            Restore
          </button>
        )}

        {/* More Options Dropdown */}
        <Dropdown
          align="right"
          trigger={
            <button className="p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors">
              <EllipsisVerticalIcon className="h-5 w-5" />
            </button>
          }
          items={[
            // Edit option for draft and active events
            ...((event.status === 'draft' || event.status === 'active') ? [{
              label: 'Edit',
              onClick: onToggleEdit,
              icon: <PencilIcon className="h-4 w-4" />
            }] : []),
            // Invite option for active events
            ...(event.status === 'active' && onInviteClick ? [{
              label: 'Invite Attendees',
              onClick: onInviteClick,
              icon: <UserPlusIcon className="h-4 w-4" />,
              className: 'text-blue-600 hover:bg-blue-50'
            }] : []),
            // Email option for events with attendees
            ...(event.approved_count > 0 ? [{
              label: 'Email Attendees',
              onClick: onEmailClick,
              icon: <EnvelopeIcon className="h-4 w-4" />,
              className: 'text-blue-600 hover:bg-blue-50'
            }] : []),
            // Email option for events with outstanding invitations
            ...(openInvitationCount > 0 && onEmailInviteesClick ? [{
              label: 'Email Invitees',
              onClick: onEmailInviteesClick,
              icon: <EnvelopeIcon className="h-4 w-4" />,
              className: 'text-purple-600 hover:bg-purple-50'
            }] : []),
            // Export option for events with registrations
            ...(event.total_registrations > 0 && onExportClick ? [{
              label: 'Export Data',
              onClick: onExportClick,
              icon: <DocumentArrowDownIcon className="h-4 w-4" />
            }] : []),
            // Sponsors for draft and active events
            ...((event.status === 'active' || event.status === 'draft') && onSponsorsClick ? [{
              label: 'Sponsors',
              onClick: onSponsorsClick,
              icon: <StarIcon className="h-4 w-4" />
            }] : []),
            // Create Media option for active and draft events
            ...((event.status === 'active' || event.status === 'draft') && onCreateMediaClick ? [{
              label: 'Create Media',
              onClick: onCreateMediaClick,
              icon: <PhotoIcon className="h-4 w-4" />,
              className: 'text-purple-600 hover:bg-purple-50'
            }] : []),
            // Unpublish option for active events
            ...(event.status === 'active' ? [{
              label: 'Unpublish',
              onClick: onToggleStatus,
              icon: <EyeSlashIcon className="h-4 w-4" />,
              className: 'text-orange-600 hover:bg-orange-50'
            }] : []),
            // Lock option for draft/active events
            ...((event.status === 'draft' || event.status === 'active') ? [{
              label: 'Lock Event',
              onClick: onArchive,
              icon: <ArchiveBoxIcon className="h-4 w-4" />,
              className: 'text-yellow-600 hover:bg-yellow-50'
            }] : []),
            // Divider before delete
            ...((event.status === 'archived' || event.status === 'draft') ? [
              { divider: true },
              {
                label: event.status === 'draft' ? 'Delete Draft' : 'Delete Permanently',
                onClick: onDelete,
                icon: <TrashIcon className="h-4 w-4" />,
                className: 'text-red-600 hover:bg-red-50'
              }
            ] : [])
          ]}
        />
      </div>
    </div>
  );
};

export default EventHeader;